import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  Baby,
  BedDouble,
  ChevronRight,
  Clock,
  Columns2,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minimize2,
  Minus,
  Monitor,
  PersonStanding,
  Plus,
  Scale,
  Sparkles,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/tummy-tuck-surgery-mumbai";

const TUMMY_TUCK_FAQS = [
  {
    question: "Is a tummy tuck the same as liposuction?",
    answer:
      "No. Liposuction removes fat only. A tummy tuck removes excess abdominal skin, repairs separated muscles, and removes fat as part of the same procedure. Patients with excess skin and muscle separation need a tummy tuck. Patients with localized fat deposits and good skin elasticity are appropriate for liposuction. The two procedures are frequently combined in the modern lipoabdominoplasty approach for patients who have both concerns.",
  },
  {
    question: "Will I need to lose weight before a tummy tuck?",
    answer:
      "Being close to your ideal weight before surgery produces the best result. Tummy tuck is not a weight loss procedure and is not appropriate for patients who are significantly overweight. Patients planning further weight loss should complete that process first, then reassess whether surgery is still needed and what the abdomen looks like at a stable weight.",
  },
  {
    question: "What is diastasis recti and can a tummy tuck fix it?",
    answer:
      "Diastasis recti is the separation of the rectus abdominis muscles along the midline, common after pregnancy. It creates a visible central bulge that exercise does not correct. Muscle repair during tummy tuck brings the separated muscles back to the midline with permanent sutures, closes the gap, narrows the waist, and restores abdominal wall integrity. This is one of the most functionally significant components of the procedure.",
  },
  {
    question: "How visible is the tummy tuck scar?",
    answer:
      "The scar runs horizontally across the lower abdomen from hip to hip. Dr. Jain places it as low as the anatomy allows, within the underwear and swimsuit line, so it is not visible in normal clothing or swimwear. The scar is initially pink and raised, then flattens and fades over 12 to 18 months. Silicone sheeting and sun protection during healing improve scar maturation.",
  },
  {
    question: "Can I have a tummy tuck if I plan to have more children?",
    answer:
      "The recommendation is to wait until your family is complete before having a tummy tuck. Pregnancy after abdominoplasty stretches the repaired muscles and tightened skin, which partially or fully reverses the surgical correction. The surgery is not unsafe to perform before a future pregnancy, but the result is not preserved through it.",
  },
  {
    question: "How long does tummy tuck recovery take?",
    answer:
      "Most patients return to desk work within 3 to 4 weeks. Exercise resumes progressively from 6 weeks onward. Full recovery with the final abdominal contour visible takes approximately 3 months as residual swelling resolves. The scar continues to mature for 12 to 18 months after surgery.",
  },
  {
    question: "Will a tummy tuck remove my stretch marks?",
    answer:
      "Stretch marks located in the skin below the navel are removed with the excised skin and are gone permanently. Stretch marks above the navel are not removed but may improve in appearance as the surrounding skin is tightened and pulled downward during the procedure.",
  },
  {
    question: "What are the risks of tummy tuck surgery?",
    answer:
      "The most common complications are seroma, a collection of fluid under the skin that requires drainage, and wound healing issues, particularly at the central point of the horizontal scar where tension is highest. Infection, bleeding, and contour irregularities are possible but less common. Smoking significantly increases the risk of wound healing complications. Choosing an experienced surgeon who uses drain management and compression correctly reduces seroma risk, which is the most frequent complication of this procedure.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Tummy tuck",
      alternateName: ["Abdominoplasty", "Abdominal contour surgery"],
      description:
        "Surgical removal of excess abdominal skin, repair of diastasis recti, and contouring of the abdomen; may combine with liposuction (lipoabdominoplasty) for waist and flank definition.",
      procedureType: "surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Abdomen" },
      howPerformed:
        "Marking upright; lower abdominal incision within underwear line; elevation of skin flap; midline rectus plication; excess skin excision; umbilical transposition; layered closure; drains; general anesthesia; 2–5 hours; often combined with liposuction.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical body contouring" },
        { "@type": "PropertyValue", name: "Not a substitute for", value: "Primary weight loss" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: TUMMY_TUCK_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function TummyTuckJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Body", href: "/#services" },
    { name: "Tummy Tuck", href: "/tummy-tuck-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Body</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Tummy Tuck Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Abdominoplasty by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Tummy Tuck" icon="💪" size="hero"  imageSrc={HERO.tummyTuck} imageAlt="Tummy tuck surgery"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What is Tummy Tuck Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A tummy tuck, clinically called abdominoplasty, removes excess abdominal skin, repairs separated or weakened abdominal
              muscles, and removes localized fat to produce a flat, firm, and well-contoured abdomen. It is not a weight loss procedure.
              It is a body contouring procedure for patients whose abdominal appearance cannot be corrected through diet, exercise, or
              liposuction alone because the problem is structural: excess skin that will not contract, muscle walls that have separated,
              or both.
            </p>
            <p>
              The most common candidates are women after pregnancy, where the abdominal skin has stretched beyond its ability to
              contract and the rectus abdominis muscles have separated along the midline, a condition called diastasis recti. Patients
              who have lost significant weight are the second most common group, where rapid fat loss leaves skin that has exceeded its
              elasticity limit and hangs from the abdominal wall. Neither group achieves the flat abdomen they are working toward
              through exercise and diet because the structural problem, skin excess and muscle separation, is not addressable without
              surgery.
            </p>
            <p>
              Dr. Vicky Jain performs tummy tuck surgery as a standalone procedure and as part of mommy makeover
              combinations that address the abdomen, breasts, and other post-pregnancy body changes together. The appropriate
              procedure type depends on the degree of skin excess, whether muscle repair is needed, and whether the flanks require
              treatment alongside the abdomen.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Abdominoplasty</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The most important technical decision in tummy tuck surgery is incision placement. The horizontal scar across the
                lower abdomen is the trade-off for everything the procedure achieves. Dr. Jain plans incision position with the
                patient&apos;s clothing and swimwear in mind, placing the scar as low as the anatomy allows so it sits entirely below
                the underwear and swimsuit line. The belly button is repositioned through a separate incision that is also concealed
                within the navel itself, leaving no visible periumbilical scar.
              </p>
              <p>
                Muscle repair is performed through the same incision before skin is removed. The separated rectus muscles are sutured
                back to the midline, which narrows the waist, flattens the abdominal wall, and strengthens the core. This component of
                the surgery produces improvements in posture and core function that patients often describe as one of the most
                significant outcomes beyond the cosmetic result. The combination of muscle repair, skin removal, and liposuction where
                needed produces a result that no single approach achieves alone.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatCorrectsSection() {
  const cards = [
    {
      title: "Loose or Hanging Abdominal Skin",
      icon: Layers,
      copy: "Excess skin that drapes below the waistline, whether from pregnancy, significant weight loss, or age-related changes, is removed surgically. This is the defining correction of a tummy tuck and cannot be achieved by any non-surgical means.",
    },
    {
      title: "Diastasis Recti",
      icon: Columns2,
      copy: "Separation of the rectus abdominis muscles along the midline, common after pregnancy, creates a visible bulge down the center of the abdomen that exercise does not correct. Muscle repair during tummy tuck brings these muscles back to the midline and restores abdominal wall integrity.",
    },
    {
      title: "Post-Pregnancy Abdominal Changes",
      icon: Baby,
      copy: "Pregnancy produces a combination of skin stretching, muscle separation, and fat accumulation that collectively cannot be reversed without surgery. Tummy tuck addresses all three in a single procedure for patients who have completed their family.",
    },
    {
      title: "Post-Weight Loss Skin Excess",
      icon: Scale,
      copy: "Rapid or significant weight loss leaves abdominal skin without the fat volume that previously supported it. The excess skin cannot retract adequately and hangs from the abdominal wall, creating hygiene problems and significant aesthetic concern. Skin removal surgery is the only solution.",
    },
    {
      title: "Stretch Marks in the Lower Abdomen",
      icon: Minimize2,
      copy: "Stretch marks located in the skin below the navel are removed with the excised skin during tummy tuck. Stretch marks above the navel are not removed but often improve in appearance as the surrounding skin is tightened.",
    },
    {
      title: "Persistent Lower Belly Bulge",
      icon: Sparkles,
      copy: "Some patients have a lower abdominal bulge that persists despite low body weight and exercise due to a combination of skin laxity and underlying fat. Tummy tuck addresses both the skin and fat components where liposuction alone would leave excess skin behind.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-corrects-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What Tummy Tuck Surgery Corrects</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DiastasisSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="diastasis-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What is Diastasis Recti and Why Does It Matter?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Diastasis recti is the separation of the two rectus abdominis muscles, which run vertically along the center of the
              abdomen. During pregnancy, the growing uterus pushes outward and stretches the connective tissue joining these muscles,
              causing them to separate. After delivery, this separation often does not close on its own. The result is a midline gap
              that causes a characteristic dome-shaped bulge down the center of the abdomen when the patient raises their head or
              performs any abdominal contraction.
            </p>
            <p>
              Exercise does not close diastasis recti and in some cases worsens the separation by increasing intra-abdominal pressure.
              The gap is not a muscular problem in the conventional sense. It is a connective tissue problem. Surgery is the only
              treatment that closes the gap reliably. During tummy tuck, Dr. Jain sutures the separated muscle edges back to the
              midline with permanent sutures, closing the gap, narrowing the waist, and restoring abdominal wall function.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const TUMMY_TYPES = [
  {
    title: "Full Tummy Tuck",
    body:
      "The most comprehensive approach. A horizontal incision runs from hip to hip low on the abdomen within the underwear line. The skin and underlying fat are elevated from the lower abdomen up to the ribcage. Muscle repair is performed. The belly button is repositioned. Excess skin is removed from below. Best for patients with moderate to significant skin excess, diastasis recti, and concerns affecting the entire abdomen from the ribs to the pubis.",
  },
  {
    title: "Mini Tummy Tuck",
    body:
      "A shorter incision and more limited skin removal targeting the lower abdomen below the navel only. The belly button is not repositioned in most cases. Muscle repair may be partial or limited. Appropriate for patients with mild lower abdominal skin excess and good upper abdominal skin quality. Not appropriate as a substitute for a full tummy tuck in patients who need more extensive correction.",
  },
  {
    title: "Extended Tummy Tuck",
    body:
      "A full tummy tuck extended laterally to include the flanks and outer thighs. The incision runs further around the hips to allow skin removal from the sides alongside the front. Best for patients with significant lateral skin excess alongside abdominal concerns, often after substantial weight loss.",
  },
  {
    title: "Circumferential Tummy Tuck (Body Lift)",
    body:
      "A 360-degree procedure that removes excess skin from the entire circumference of the lower trunk, including the abdomen, flanks, back, and outer thighs. Used for patients after massive weight loss where skin excess is present all the way around the body, not just on the front. The most extensive lower body contouring procedure.",
  },
  {
    title: "Tummy Tuck with Liposuction (Lipoabdominoplasty)",
    body:
      "The standard modern approach combines liposuction to remove fat from the flanks, waist, and upper abdomen with the skin removal and muscle repair of a traditional tummy tuck. The combination produces better overall waist definition and abdominal contour than either procedure alone. Dr. Jain uses this combined approach as the default for most full tummy tuck patients.",
  },
];

function TypesSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Types of Tummy Tuck Surgery</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {TUMMY_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Tummy Tuck Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 2 to 5 hours under general anesthesia depending on the extent of the procedure and whether liposuction is
              being performed simultaneously. Dr. Jain marks the planned incision, the extent of skin to be removed, and all liposuction
              areas with the patient standing upright before surgery.
            </p>
            <p>
              The incision is made across the lower abdomen within the planned underwear line. The abdominal skin is elevated from the
              muscle fascia up to the ribcage and laterally to the sides of the waist, depending on the extent of the procedure.
              Muscle repair is performed at this stage: the separated rectus muscles are brought back to the midline and sutured with
              permanent sutures that narrow the waist and restore abdominal wall integrity.
            </p>
            <p>
              The elevated skin flap is pulled downward under slight tension, the excess is marked and excised, and the remaining skin
              is sutured in layers to the lower incision. The belly button is brought through a new opening in the repositioned skin
              and sutured into a natural position. Drains are placed beneath the skin flap and exit through small openings within the
              scar line. They are removed at the first post-operative visit when drainage decreases to an acceptable level, typically
              within 5 to 7 days.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Week 1",
      description:
        "Moderate discomfort, swelling, and tightness across the abdomen. Drains in place. Patients walk slightly bent at the hips to reduce tension on the incision. This posture gradually straightens over the first week as swelling and tightness reduce.",
      icon: BedDouble,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Drains removed. Light daily activity resumes. Most patients describe a significant reduction in discomfort by day 10 to 14. The abdomen looks flatter even through residual swelling.",
      icon: Monitor,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "Most patients return to desk work. Normal walking posture fully restored. Abdominal binder continues. Strenuous activity and heavy lifting remain restricted.",
      icon: PersonStanding,
    },
    {
      period: "Weeks 6 to 8 and Beyond",
      description:
        "Exercise resumes progressively. Final abdominal contour becomes visible as swelling fully resolves. The scar begins the maturation process that continues over 12 to 18 months.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_ROWS = [
  {
    label: "Skin Removal",
    tuck: "Yes. Excess abdominal skin is surgically removed.",
    lipo: "No. Fat is removed but skin must contract on its own.",
  },
  {
    label: "Muscle Repair",
    tuck: "Yes. Separated abdominal muscles are repaired.",
    lipo: "No.",
  },
  {
    label: "Fat Removal",
    tuck: "Yes, as part of the procedure or combined with liposuction.",
    lipo: "Yes, the primary function.",
  },
  {
    label: "Belly Button Repositioning",
    tuck: "Yes, in full tummy tuck.",
    lipo: "No.",
  },
  {
    label: "Scar",
    tuck: "Long horizontal scar across lower abdomen, hidden within underwear line.",
    lipo: "Small 2 to 5 millimeter access points only.",
  },
  {
    label: "Best For",
    tuck: "Patients with excess abdominal skin, muscle separation, and fat after pregnancy or weight loss.",
    lipo: "Patients with localized fat deposits and good skin elasticity who do not have skin excess.",
  },
];

function TummyTuckVsLipoSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="tt-vs-lipo-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">
            Tummy Tuck vs. Liposuction: Which Do You Need?
          </h2>
        </div>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs sm:text-sm md:text-base min-w-[640px]">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-3 pr-2 sm:pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-3 px-2 sm:px-3 font-serif text-premium-purple bg-white/80">
                  Tummy Tuck
                </th>
                <th scope="col" className="py-3 pl-2 sm:pl-3 font-serif text-premium-purple bg-white/80">
                  Liposuction
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-3 pr-2 sm:pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-3 px-2 sm:px-3 text-deep-navy/70 align-top">{row.tuck}</td>
                  <td className="py-3 pl-2 sm:pl-3 text-deep-navy/70 align-top">{row.lipo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center leading-relaxed text-sm md:text-base">
          The most common mistake in abdominal contouring is performing liposuction on a patient who needs a tummy tuck. Removing fat
          from an abdomen with poor skin elasticity and no muscle repair leaves the skin with less volume underneath it but no
          improvement in the skin excess. The result is a flatter contour with worsened skin laxity. Dr. Jain assesses skin quality and
          muscle integrity during every abdominal consultation to determine which procedure correctly addresses the problem.
        </p>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Tummy Tuck Surgery?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are adults who have completed their family, are at a stable weight for at least 3 to 6 months, and have
              specific abdominal concerns that cannot be addressed without surgery: excess skin, muscle separation, or a combination of
              both. The most important requirement is that future pregnancy is not planned. Pregnancy after a tummy tuck stretches the
              repaired muscles and skin again, undoing the surgical correction. Patients who have not yet completed their family are
              advised to delay surgery.
            </p>
            <p>
              Patients should be close to their ideal weight before tummy tuck, not planning to lose significant additional weight
              after surgery. Weight loss after a tummy tuck reduces the fat volume beneath the tightened skin and can create new
              laxity. Conversely, weight gain stretches the corrected abdominal wall. The best long-term result comes from a patient who
              is at a stable, sustainable weight at the time of surgery and maintains it afterward. Non-smokers or patients who stop
              smoking at least 4 weeks before surgery have significantly lower rates of wound healing complications, which are the most
              common complication of tummy tuck surgery. Patients with significant obesity are better served by weight reduction before
              considering abdominoplasty.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const PatientResultsSection = () => <PatientTestimonials procedureName="Tummy Tuck" />;

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {TUMMY_TUCK_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`tt-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Tummy Tuck Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your abdominal skin quality, muscle separation, fat distribution, and overall
          body proportions to determine the appropriate tummy tuck technique and whether liposuction should be combined for the best
          result. You leave with a clear surgical plan, a realistic picture of the scar and outcome, and a full understanding of the
          recovery process.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Liposuction",
      description: "Fat removal from flanks, waist, and other areas to complement tummy tuck contouring.",
      href: "/liposuction-surgery-mumbai",
    },
    {
      name: "Breast Lift",
      description: "Address breast position and shape alongside abdominal correction in a mommy makeover.",
      href: "/breast-lift-surgery-mumbai",
    },
    {
      name: "Mommy Makeover",
      description: "Complete post-pregnancy body restoration addressing abdomen, breasts, and body contour in a single surgery.",
      href: "/mommy-makeover-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Related Procedures</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Related Procedures</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function TummyTuckPage() {
  return (
    <>
      <SEO
        title="Tummy Tuck Surgery | Abdominoplasty by Dr. Vicky Jain"
        description="Loose abdominal skin, muscle separation, and stubborn belly fat after pregnancy or weight loss. Dr. Vicky Jain performs tummy tuck surgery. Book today."
        canonical="/tummy-tuck-surgery-mumbai"
      />
      <TummyTuckJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatCorrectsSection />
      <DiastasisSection />
      <TypesSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <TummyTuckVsLipoSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
